import { Injectable } from '@angular/core';
import { Color, Style } from 'src/app/components/company/models/new/style';
import { IFilterListService } from './ifilter-list.service';
import { StyleMapper } from 'src/app/components/company/services/style-mapper';
import { DatabaseQueries, SharedQueries } from 'src/app/shared/types/get-query';
import { map } from 'rxjs';
import { Selectable } from 'src/app/shared/types/selectable.type';

@Injectable({
	providedIn: 'root'
})
export class StyleFilterService extends IFilterListService<Style> {
	private readonly PAGE_SIZE = 50;

	private currentSearch = '';
	private currentPage = 0;

	private hasNextPage = true;

	private currentVariables: any = {};
	private internalDatasource: Selectable<Style>[] = [];

	constructor(private styleMapper: StyleMapper) {
		super();
	}

	pageCursorCalculator(pageIndex: number, pageSize: number): string {
		return btoa((pageSize * pageIndex - 1).toString());
	}

	reset(): void {
		this.currentPage = 0;
		this.hasNextPage = true;
		this.internalDatasource = [];
		this.datasource.next([]);
	}

	search(searchText: string, variables?: any, append: boolean = false): void {
		if (this.loading) return;
		if (append && !this.hasNextPage) return;

		if (!append) {
			this.reset();
			this.currentSearch = searchText;

			// NON perdere mai le variables
			if (variables) {
				this.currentVariables = variables;
			}
		}

		const after = this.currentPage === 0 ? null : this.pageCursorCalculator(this.currentPage, this.PAGE_SIZE);

		const where: any = {
			and: [
				{
					code: {
						contains: this.currentSearch
					}
				}
			]
		};

		// aggiungi filtro SOLO se esiste davvero
		if (this.currentVariables?.categoryIds != null) {
			where.and.push({
				categoryId: {
					in: this.currentVariables?.categoryIds
				}
			});
		}

		if (this.currentVariables?.brandIds != null) {
			where.and.push({
				brandId: {
					in: this.currentVariables?.brandIds
				}
			});
		}

		this.loading = true;
		this.graphQlService
			.getData<any>(DatabaseQueries.GET_STYLES, {
				where,
				first: this.PAGE_SIZE,
				after
			})
			.pipe(
				map((res: any) =>
					res.data.styles.nodes.map((node: any) => ({
						...this.styleMapper.toStyle(node),
						displayId: node.modelId,
						displayName: `${node.code} - ${node.name}`,
						selected: false,
						disabled: false
					}))
				)
			)
			.subscribe({
				next: (items: Selectable<Style>[]) => {
				this.loading = false;
				if (!items.length) {
						this.hasNextPage = false;
						this.datasource.next(this.internalDatasource);
						return;
					}

					const unique = items.filter((item) => !this.internalDatasource.some((el) => el.displayId === item.displayId));

					this.internalDatasource = [...this.internalDatasource, ...unique];

					this.datasource.next(this.internalDatasource);

					this.currentPage++;

					if (items.length < this.PAGE_SIZE) {
						this.hasNextPage = false;
					}

				},
				error: () => {
					this.loading = false;
				}
			});
	}

	loadNextPage(): void {
		this.search(this.currentSearch, this.currentVariables, true);
	}

	preloadUntilSelected(selected: Array<Selectable<Style>>, searchText: string = '', variables?: any): void {
		if (!selected?.length) {
			this.search(searchText, variables);
			return;
		}

		this.reset();

		this.currentVariables = variables || {};

		const targetIds = selected.map((el) => el.displayId);

		const recursiveLoad = () => {
			if (this.loading || !this.hasNextPage) return;

			const after = this.currentPage === 0 ? null : this.pageCursorCalculator(this.currentPage, this.PAGE_SIZE);

			this.loading = true;

			const where: any = {
				and: [
					{
						code: {
							contains: searchText
						}
					}
				]
			};

			// aggiungi filtro SOLO se esiste davvero
			if (this.currentVariables?.categoryIds != null) {
				where.and.push({
					categoryId: {
						in: this.currentVariables?.categoryIds
					}
				});
			}

			if (this.currentVariables?.brandIds != null) {
				where.and.push({
					brandId: {
						in: this.currentVariables?.brandIds
					}
				});
			}

			this.graphQlService
				.getData<any>(DatabaseQueries.GET_STYLES, {
					where,
					first: this.PAGE_SIZE,
					after
				})
				.pipe(
					map((res: any) =>
						res.data.styles.nodes.map((node: any) => ({
							...this.styleMapper.toStyle(node),
							displayId: node.modelId,
							displayName: `${node.code} - ${node.name}`,
							selected: false,
							disabled: false
						}))
					)
				)
				.subscribe({
					next: (items: Selectable<Style>[]) => {
						if (!items.length) {
							this.hasNextPage = false;
							this.loading = false;
							this.datasource.next(this.internalDatasource);
							return;
						}

						const unique = items.filter((item) => !this.internalDatasource.some((el) => el.displayId === item.displayId));

						this.internalDatasource = [...this.internalDatasource, ...unique];

						this.datasource.next(this.internalDatasource);

						this.currentPage++;

						const foundAll = targetIds.every((id) => this.internalDatasource.some((el) => el.displayId === id));

						if (foundAll) {
							this.loading = false;
							return;
						}

						if (items.length < this.PAGE_SIZE) {
							this.hasNextPage = false;
							this.loading = false;
							return;
						}

						this.loading = false;
						recursiveLoad();
					},
					error: () => {
						this.loading = false;
					}
				});
		};

		recursiveLoad();
	}

	searchById(_id: string[] | string) {
		const ids = Array.isArray(_id) ? _id.map((id) => Number(id)) : [Number(_id)];

		return this.graphQlService
			.getData<any>(DatabaseQueries.GET_STYLES, {
				where: {
					modelId: {
						in: ids
					}
				},
				first: ids.length
			})
			.pipe(map((res) => res.data.styles.nodes.map((el: any) => this.styleMapper.toStyle(el))));
	}
}